import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { Bell, Download, RefreshCw, CheckCircle, XCircle, AlertTriangle, Clock, Loader2 } from 'lucide-react';

const STATUS_META = {
  delivered: { label: 'Entregada', color: 'text-[#A3FF12]', bg: 'bg-[#A3FF12]/10 border-[#A3FF12]/30', Icon: CheckCircle },
  failed:    { label: 'Fallo',     color: 'text-red-300',  bg: 'bg-red-500/10 border-red-500/30',    Icon: XCircle },
  expired:   { label: 'Caducada',  color: 'text-white/50', bg: 'bg-white/5 border-white/10',         Icon: Clock },
  error:     { label: 'Error',     color: 'text-orange-300', bg: 'bg-orange-500/10 border-orange-500/30', Icon: AlertTriangle },
};

const PushLog = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [days, setDays] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      const params = { days };
      if (status) params.status = status;
      const [r, s] = await Promise.all([
        api.get('/push/log', { params }),
        api.get('/push/log/summary'),
      ]);
      setItems(r.data.items || []);
      setSummary(s.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, days]);

  const exportCsv = async () => {
    const r = await api.get('/push/log/export.csv', { params: { days }, responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url; a.download = `push-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="push-log-page">
      <div className="max-w-6xl mx-auto p-4 sm:p-8">
        <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-bold tracking-widest text-[#A3FF12]">ADMIN</div>
            <h1 className="text-3xl sm:text-4xl font-black mt-1 inline-flex items-center gap-3"><Bell className="text-[#A3FF12]"/>Notificaciones push</h1>
            <p className="text-white/50 text-sm mt-2">Histórico de avisos enviados y estado de entrega.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="border border-white/10 rounded-lg px-3 py-2 text-xs font-bold hover:bg-white/5 inline-flex items-center gap-1"><RefreshCw size={12}/> Refrescar</button>
            <button onClick={exportCsv} className="bg-[#A3FF12] text-black rounded-lg px-3 py-2 text-xs font-bold hover:bg-[#b8ff3a] inline-flex items-center gap-1" data-testid="btn-export-push"><Download size={12}/> Exportar CSV</button>
          </div>
        </header>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <Stat label="Últimos 7 días" value={summary.total} accent="white"/>
            <Stat label="Entregadas" value={summary.delivered} accent="green"/>
            <Stat label="Fallidas" value={summary.failed} accent="red"/>
            <Stat label="Caducadas" value={summary.expired} accent="gray"/>
            <Stat label="Tasa entrega" value={`${summary.delivery_rate}%`} accent="green"/>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-4 items-center">
          <span className="text-white/40 text-xs mr-2">Estado:</span>
          {['', 'delivered', 'failed', 'expired', 'error'].map((s) => (
            <button key={s || 'all'} onClick={() => setStatus(s)}
              data-testid={`filter-${s || 'all'}`}
              className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border ${status === s ? 'bg-[#A3FF12]/20 text-[#A3FF12] border-[#A3FF12]/40' : 'bg-white/5 text-white/50 border-white/10 hover:bg-white/10'}`}>
              {s === '' ? 'Todos' : (STATUS_META[s]?.label || s)}
            </button>
          ))}
          <span className="text-white/40 text-xs ml-4 mr-2">Rango:</span>
          <select value={days} onChange={(e) => setDays(+e.target.value)} className="bg-black border border-white/10 rounded-lg px-2 py-1 text-xs text-white outline-none">
            <option value={7}>7 días</option>
            <option value={30}>30 días</option>
            <option value={90}>90 días</option>
            <option value={365}>365 días</option>
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-[#A3FF12]" size={24}/></div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-white/40">Sin envíos en este rango.</div>
        ) : (
          <div className="space-y-1.5">
            {items.map((it) => {
              const meta = STATUS_META[it.status] || STATUS_META.error;
              const Icon = meta.Icon;
              return (
                <div key={it.id} className={`border rounded-lg px-3 py-2 flex items-start gap-3 ${meta.bg}`} data-testid={`push-log-row-${it.id}`}>
                  <Icon className={`${meta.color} flex-shrink-0 mt-0.5`} size={16}/>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[9px] font-black uppercase tracking-widest ${meta.color}`}>{meta.label}</span>
                      <span className="text-white text-sm font-bold truncate">{it.title}</span>
                    </div>
                    <div className="text-white/60 text-xs truncate">{it.body}</div>
                    {it.error && <div className="text-red-300 text-[11px] mt-0.5 truncate italic">{it.error}</div>}
                  </div>
                  <div className="text-right text-[11px] text-white/50 flex-shrink-0">
                    <div className="font-mono">{new Date(it.sent_at).toLocaleString('es-ES')}</div>
                    <div>{it.user_name || <span className="text-white/30">—</span>}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, accent }) => {
  const cls = {
    green: 'text-[#A3FF12] border-[#A3FF12]/30 bg-[#A3FF12]/5',
    red:   'text-red-300 border-red-500/30 bg-red-500/5',
    gray:  'text-white/60 border-white/10 bg-white/5',
    white: 'text-white border-white/10 bg-white/5',
  }[accent] || 'text-white border-white/10 bg-white/5';
  return (
    <div className={`border rounded-xl px-3 py-2 ${cls}`}>
      <div className="text-[10px] font-black uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-2xl font-black">{value}</div>
    </div>
  );
};

export default PushLog;
